import Phaser from 'phaser';
import { bus, Events } from './events';
import { SceneKeys, type SceneKey } from './scenes/keys';
import type { SceneEntryData } from './types';
import { entriesFor, type LoadGroup, type ManifestEntry } from '@/content/manifest';
import type { LoadingData } from './scenes/LoadingScene';

/** Manifest group loaded before each gameplay scene starts. */
const GROUP_FOR_SCENE: Partial<Record<SceneKey, LoadGroup>> = {
  [SceneKeys.PROLOGUE]: 'prologue',
  [SceneKeys.GREEN_HARBOR]: 'green-harbor',
  [SceneKeys.TURTLE_INN]: 'turtle-inn',
  [SceneKeys.BATTLE]: 'battle',
};

export interface RouterOptions { fadeMs?: number }

/** The only sanctioned way to move between gameplay scenes. UIScene stays alive across transitions. */
export class Router {
  private busy = false;
  constructor(private readonly game: Phaser.Game) {}
  isBusy(): boolean { return this.busy; }

  current(): Phaser.Scene | null {
    const active = this.game.scene.getScenes(true).filter((s) => s.scene.key !== SceneKeys.UI && s.scene.key !== SceneKeys.LOADING);
    // Prefer a world scene (has `map` + `actors`) over a showcase host; else the most recently started.
    const world = active.find((s) => (s as unknown as { map?: unknown; actors?: unknown }).map && (s as unknown as { actors?: unknown }).actors);
    return world ?? active[active.length - 1] ?? null;
  }

  /** Every gameplay scene that is active OR paused (e.g. a scene underneath a memory stage). */
  private liveScenes(): Phaser.Scene[] {
    return this.game.scene.getScenes(false).filter((s) => {
      const k = s.scene.key;
      if (k === SceneKeys.UI || k === SceneKeys.LOADING) return false;
      return s.scene.isActive() || s.scene.isPaused() || s.scene.isSleeping();
    });
  }

  async go(target: SceneKey, data: SceneEntryData = {}, opts: RouterOptions = {}): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const fadeMs = opts.fadeMs ?? 350;
    const from = this.current();
    try {
      await this.fade(true, fadeMs);
      // Leave the Phaser update loop (tween callbacks run inside SceneManager processing) so that
      // stop/start are applied directly and deterministically rather than queued mid-iteration.
      await new Promise<void>((r) => setTimeout(r, 0));
      const live = this.liveScenes();
      if (from) bus.emitTyped(Events.SCENE_EXIT, { key: from.scene.key });
      for (const s of live) this.game.scene.stop(s.scene.key);
      // Same-scene re-entry: stop+start in one tick leaves no active scene; wait a frame first.
      if (live.some((s) => s.scene.key === target)) await new Promise<void>((r) => setTimeout(r, 20));
      // Chapter assets: load the target's manifest group (real progress bar) before the scene starts.
      await this.loadGroupFor(target);
      this.game.scene.start(target, data);
      bus.emitTyped(Events.SCENE_ENTER, { key: target });
      await this.waitForCreate(target);
      await this.fade(false, fadeMs);
    } finally {
      this.busy = false;
    }
  }

  /** Loads the manifest group owned by a scene key through LoadingScene; resolves when done (or immediately). */
  private loadGroupFor(target: SceneKey): Promise<void> {
    const group = GROUP_FOR_SCENE[target];
    if (!group) return Promise.resolve();
    return this.loadGroup(group);
  }

  /** Public: load any manifest group (e.g. 'battle' before launching the battle scene in parallel). */
  loadGroup(group: LoadGroup): Promise<void> {
    const pending = entriesFor(group).filter((e) => !this.isLoaded(e));
    if (pending.length === 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const done = () => resolve();
      this.game.scene.start(SceneKeys.LOADING, { group, onDone: done } satisfies LoadingData);
      setTimeout(done, 30000); // never hang on a broken asset
    });
  }

  private isLoaded(e: ManifestEntry): boolean {
    switch (e.type) {
      case 'audio': return this.game.cache.audio.exists(e.key);
      case 'json': return this.game.cache.json.exists(e.key);
      case 'tilemapTiledJSON': return this.game.cache.tilemap.exists(e.key);
      default: return this.game.textures.exists(e.key);
    }
  }

  private waitForCreate(key: SceneKey): Promise<void> {
    return new Promise((resolve) => {
      const scene = this.game.scene.getScene(key);
      if (!scene) return resolve();
      const st = scene.scene.settings.status;
      if (st === Phaser.Scenes.RUNNING || st === Phaser.Scenes.PAUSED) return resolve();
      scene.events.once(Phaser.Scenes.Events.CREATE, () => resolve());
      // Guard against scenes that never create (e.g. errors): resolve after a frame budget.
      setTimeout(resolve, 2000);
    });
  }

  private fade(out: boolean, ms: number): Promise<void> {
    const ui = this.game.scene.getScene(SceneKeys.UI) as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;
    if (ui?.fade && ui.scene.isActive()) return ui.fade(out, ms);
    return Promise.resolve();
  }
}
